import {
    AlertCircle,
    Check,
    CheckCircle,
    ChevronDown,
    ChevronUp,
    Download,
    Edit,
    Filter,
    Percent,
    Plus,
    RefreshCw,
    Users,
    X,
} from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import PageGuide from '../components/analytics/PageGuide';
import {
    commercialCommissionsService,
    deviceService,
    tenantService,
    venueService,
} from '../services/api';
import './CommercialCommissions.css';

const formatCurrency = (cents) => {
    if (cents == null) return '—';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(cents / 100);
};

const formatMonth = (isoDate) => {
    if (!isoDate) return '—';
    const s = String(isoDate).substring(0, 7);
    return new Date(`${s}-02`).toLocaleDateString('it-IT', { year: 'numeric', month: 'long' });
};

const formatDate = (isoDate) => {
    if (!isoDate) return '—';
    return new Date(isoDate).toLocaleDateString('it-IT');
};

const formatPercent = (value) => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return `${Number(value).toFixed(2)}%`;
};

const getCurrentYearMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const getYearMonthOffset = (offset) => {
    const now = new Date();
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth() + offset, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

const listMonthsBetween = (startYm, endYm) => {
    const [sy, sm] = String(startYm).split('-').map(Number);
    const [ey, em] = String(endYm).split('-').map(Number);
    const start = new Date(Date.UTC(sy, sm - 1, 1));
    const end = new Date(Date.UTC(ey, em - 1, 1));
    const out = [];
    const cur = new Date(start);
    while (cur <= end) {
        out.push(`${cur.getUTCFullYear()}-${String(cur.getUTCMonth() + 1).padStart(2, '0')}`);
        cur.setUTCMonth(cur.getUTCMonth() + 1);
    }
    return out;
};

const getNextEffectiveYearMonth = () => {
    const now = new Date();
    const next = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 1));
    return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}`;
};

const toYearMonthKey = (value) => (value ? String(value).substring(0, 7) : '');

const normalizeTenants = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.tenants)) return data.tenants;
    if (Array.isArray(data.data)) return data.data;
    return [];
};

const normalizeVenues = (data) => data?.venues || data?.data || [];
const normalizeDevices = (data) => data?.devices || data?.data || [];

const getRuleStatusMeta = (rule) => {
    const currentMonth = getCurrentYearMonth();
    const fromMonth = toYearMonthKey(rule.effectiveFromMonth);
    const toMonth = toYearMonthKey(rule.effectiveToMonth);

    if (rule.active === false && (!toMonth || toMonth <= currentMonth)) {
        return { label: 'Non attiva', className: 'cc-badge-inactive' };
    }
    if (fromMonth > currentMonth) {
        return { label: `Attiva da ${formatMonth(rule.effectiveFromMonth)}`, className: 'cc-badge-calculated' };
    }
    if (!toMonth || toMonth > currentMonth) {
        return { label: 'Attiva', className: 'cc-badge-active' };
    }
    return { label: 'Non attiva', className: 'cc-badge-inactive' };
};

function StatusBadge({ status }) {
    if (status === 'PAID') {
        return (
            <span className="cc-badge cc-badge-paid">
                <CheckCircle size={11} />
                Pagata
            </span>
        );
    }

    return (
        <span className="cc-badge cc-badge-calculated">
            <RefreshCw size={11} />
            Da pagare
        </span>
    );
}

function SortTh({ field, sortBy, sortDir, onSort, alignRight = false, children }) {
    const active = sortBy === field;
    return (
        <th className={alignRight ? 'cc-th-right' : ''}>
            <button
                type="button"
                className={`cc-th-sort-btn${active ? ' cc-th-sort-btn-active' : ''}`}
                onClick={() => onSort(field)}
            >
                <span>{children}</span>
                {active ? (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : <ChevronDown size={12} className="cc-sort-idle" />}
            </button>
        </th>
    );
}

export default function CommercialCommissions() {
    const [monthlyData, setMonthlyData] = useState([]);
    const [agents, setAgents] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [rules, setRules] = useState([]);
    const [venues, setVenues] = useState([]);
    const [devices, setDevices] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [generatingRetroactive, setGeneratingRetroactive] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [selectedMonth, setSelectedMonth] = useState(getCurrentYearMonth());
    const [periodPreset, setPeriodPreset] = useState('EXACT_MONTH');
    const [filterTenantId, setFilterTenantId] = useState('');
    const [filterVenueId, setFilterVenueId] = useState('');
    const [filterDeviceId, setFilterDeviceId] = useState('');
    const [filterAgentId, setFilterAgentId] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    const [showManageModal, setShowManageModal] = useState(false);
    const [manageTab, setManageTab] = useState('agents');
    const [confirmPaidItem, setConfirmPaidItem] = useState(null);
    const [markingPaid, setMarkingPaid] = useState(false);

    const [editingAgent, setEditingAgent] = useState(null);
    const [agentForm, setAgentForm] = useState({ name: '', email: '', code: '' });
    const [agentFormError, setAgentFormError] = useState('');
    const [agentSaving, setAgentSaving] = useState(false);
    const [showAgentForm, setShowAgentForm] = useState(false);

    const [editingRule, setEditingRule] = useState(null);
    const [ruleForm, setRuleForm] = useState({ tenantId: '', commercialAgentId: '', commissionPercent: '' });
    const [ruleFormError, setRuleFormError] = useState('');
    const [ruleSaving, setRuleSaving] = useState(false);
    const [showRuleForm, setShowRuleForm] = useState(false);

    const [sortBy, setSortBy] = useState('tenant');
    const [sortDir, setSortDir] = useState('asc');
    const [expandedRowId, setExpandedRowId] = useState(null);
    const isExactMonthMode = periodPreset === 'EXACT_MONTH';

    const monthOptions = useMemo(() => {
        const current = getCurrentYearMonth();
        let firstRuleMonth = current;
        if (rules.length > 0) {
            const sorted = [...rules]
                .map((r) => toYearMonthKey(r.effectiveFromMonth))
                .filter(Boolean)
                .sort();
            if (sorted.length > 0) firstRuleMonth = sorted[0];
        }

        if (periodPreset === 'LAST_MONTH') return [getYearMonthOffset(-1)];
        if (periodPreset === 'LAST_3_MONTHS') {
            return [getYearMonthOffset(-2), getYearMonthOffset(-1), getYearMonthOffset(0)];
        }
        if (periodPreset === 'ALL') {
            return listMonthsBetween(firstRuleMonth, current);
        }
        return [selectedMonth];
    }, [periodPreset, selectedMonth, rules]);

    const loadMonthlyData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const requests = monthOptions.map(async (month) => {
                const params = { month };
                if (filterTenantId) params.tenantId = filterTenantId;
                if (filterVenueId) params.venueId = filterVenueId;
                if (filterDeviceId) params.deviceId = filterDeviceId;
                if (filterAgentId) params.commercialAgentId = filterAgentId;
                if (filterStatus) params.status = filterStatus;
                return commercialCommissionsService.getMonthly(params);
            });

            const monthlyResponses = await Promise.all(requests);
            const flat = [];

            for (const data of monthlyResponses) {
                for (const tenantEntry of (data.tenants || [])) {
                    for (const commission of (tenantEntry.commissions || [])) {
                        flat.push({
                            id: commission.id,
                            periodMonth: data.month,
                            tenantId: tenantEntry.tenantId,
                            tenant: { name: tenantEntry.tenantName },
                            gtvCents: tenantEntry.gtvCents,
                            estimatedSoontipFeeCents: tenantEntry.estimatedSoontipFeeCents,
                            residualSoontipAfterCommissionsCents: tenantEntry.residualSoontipAfterCommissionsCents,
                            totalCommissionCents: tenantEntry.totalCommissionCents,
                            totalPercentAssigned: tenantEntry.totalPercentAssigned,
                            commercialAgentsCount: tenantEntry.commercialAgentsCount,
                            venueBreakdown: tenantEntry.venueBreakdown || [],
                            tenantCommissionQuotes: tenantEntry.commissions || [],
                            commercialAgentId: commission.commercialAgentId,
                            agent: {
                                name: commission.agentName,
                                code: commission.agentCode,
                                email: commission.agentEmail,
                            },
                            commissionPercentSnapshot: commission.commissionPercentSnapshot,
                            commissionAmountCents: commission.commissionAmountCents,
                            status: commission.status,
                            calculatedAt: commission.calculatedAt,
                            paidAt: commission.paidAt,
                            paidByAdminId: commission.paidByAdminId,
                        });
                    }
                }
            }

            setMonthlyData(flat);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Errore nel caricamento');
        } finally {
            setLoading(false);
        }
    }, [monthOptions, filterTenantId, filterVenueId, filterDeviceId, filterAgentId, filterStatus]);

    const loadAgents = useCallback(async () => {
        try {
            const data = await commercialCommissionsService.getAgents({ includeInactive: true });
            setAgents(data.data || data.agents || []);
        } catch (err) {
            console.error('Failed to load agents:', err);
        }
    }, []);

    const loadTenants = useCallback(async () => {
        try {
            const data = await tenantService.getTenants();
            setTenants(normalizeTenants(data));
        } catch (err) {
            console.error('Failed to load tenants:', err);
        }
    }, []);

    const loadRules = useCallback(async () => {
        try {
            const data = await commercialCommissionsService.getRules({ activeOnly: false });
            setRules(data.data || data.rules || []);
        } catch (err) {
            console.error('Failed to load rules:', err);
        }
    }, []);

    const loadFilterOptions = useCallback(async () => {
        try {
            const [venueData, deviceData] = await Promise.all([
                venueService.getVenues({ tenantId: filterTenantId || '', limit: 1000 }),
                deviceService.getDevices({ tenantId: filterTenantId || '', limit: 1000 }),
            ]);
            const nextVenues = normalizeVenues(venueData);
            const nextDevices = normalizeDevices(deviceData);

            setVenues(nextVenues);
            setDevices(nextDevices);

            if (filterVenueId && !nextVenues.some((venue) => venue.id === filterVenueId)) {
                setFilterVenueId('');
                setFilterDeviceId('');
            }

            if (filterDeviceId && !nextDevices.some((device) => device.id === filterDeviceId)) {
                setFilterDeviceId('');
            }
        } catch (err) {
            console.error('Failed to load filter options:', err);
        }
    }, [filterTenantId, filterVenueId, filterDeviceId]);

    useEffect(() => {
        loadMonthlyData();
    }, [loadMonthlyData]);

    useEffect(() => {
        loadAgents();
        loadTenants();
        loadRules();
    }, [loadAgents, loadTenants, loadRules]);

    useEffect(() => {
        loadFilterOptions();
    }, [loadFilterOptions]);

    const handleGenerate = async () => {
        if (periodPreset !== 'EXACT_MONTH') {
            setError('La generazione snapshot è disponibile solo con filtro periodo "Mese specifico".');
            return;
        }

        if (!window.confirm(
            `Generare / aggiornare il calcolo per ${selectedMonth}?\n\nLe righe già segnate come "Pagata" non verranno modificate.`
        )) {
            return;
        }

        setGenerating(true);
        setError(null);
        try {
            const params = { month: selectedMonth };
            if (filterTenantId) params.tenantId = filterTenantId;
            await commercialCommissionsService.generateSnapshot(params);
            await loadMonthlyData();
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Errore nella generazione');
        } finally {
            setGenerating(false);
        }
    };

    const handleGenerateRetroactive = async () => {
        if (!isExactMonthMode) {
            setError('La generazione retroattiva è disponibile solo con filtro periodo "Mese specifico".');
            return;
        }

        const confirmed = window.confirm(
            `Generare RETROATTIVAMENTE il calcolo per ${selectedMonth}?\n\n` +
            `La retroattiva usa le regole attive nel mese target; se assenti, usa fallback alle regole correnti/ultime note. ` +
            `Le righe già "Pagata" non vengono modificate.`
        );
        if (!confirmed) return;

        setGeneratingRetroactive(true);
        setError(null);
        try {
            const params = { month: selectedMonth };
            if (filterTenantId) params.tenantId = filterTenantId;
            await commercialCommissionsService.generateSnapshotRetroactive(params);
            await loadMonthlyData();
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Errore nella generazione retroattiva');
        } finally {
            setGeneratingRetroactive(false);
        }
    };

    const exportVisibleRowsCsv = () => {
        const escapeCsv = (value) => {
            const text = value == null ? '' : String(value);
            if (!/[;"\n]/.test(text)) return text;
            return `"${text.replace(/"/g, '""')}"`;
        };

        const header = [
            'Mese',
            'Tenant',
            'Commerciale',
            'Codice Commerciale',
            'Fee Soontip stimata',
            'Percentuale commerciale',
            'Provvigione calcolata',
            'Stato',
            'Data pagamento',
            'Data calcolo',
        ];

        const rows = filteredData.map((row) => [
            row.periodMonth,
            row.tenant?.name || row.tenantId,
            row.agent?.name || row.commercialAgentId,
            row.agent?.code || '',
            ((row.estimatedSoontipFeeCents || 0) / 100).toFixed(2).replace('.', ','),
            Number(row.commissionPercentSnapshot || 0).toFixed(2),
            ((row.commissionAmountCents || 0) / 100).toFixed(2).replace('.', ','),
            row.status === 'PAID' ? 'Pagata' : 'Da pagare',
            row.paidAt ? new Date(row.paidAt).toISOString().split('T')[0] : '',
            row.calculatedAt ? new Date(row.calculatedAt).toISOString().split('T')[0] : '',
        ].map(escapeCsv).join(';'));

        const csv = [header.join(';'), ...rows].join('\n');
        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `provvigioni-${periodPreset === 'EXACT_MONTH' ? selectedMonth : 'multi-mese'}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleExport = async () => {
        if (periodPreset !== 'EXACT_MONTH') {
            exportVisibleRowsCsv();
            return;
        }

        if (!filterAgentId) {
            const confirmed = window.confirm(
                'Stai esportando tutte le provvigioni visibili. Per inviare il prospetto a un singolo commerciale, seleziona prima il commerciale. Vuoi continuare?'
            );
            if (!confirmed) return;
        }

        setExporting(true);
        try {
            const params = { month: selectedMonth };
            if (filterTenantId) params.tenantId = filterTenantId;
            if (filterVenueId) params.venueId = filterVenueId;
            if (filterDeviceId) params.deviceId = filterDeviceId;
            if (filterAgentId) params.commercialAgentId = filterAgentId;
            if (filterStatus) params.status = filterStatus;

            const blob = await commercialCommissionsService.exportCsv(params);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `provvigioni-${selectedMonth}.csv`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Errore nell'export");
        } finally {
            setExporting(false);
        }
    };

    const handleMarkPaid = async () => {
        if (!confirmPaidItem) return;

        setMarkingPaid(true);
        try {
            await commercialCommissionsService.markAsPaid(confirmPaidItem.id);
            setConfirmPaidItem(null);
            await loadMonthlyData();
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Errore nel segnare come pagata la quota');
        } finally {
            setMarkingPaid(false);
        }
    };

    const openNewAgentForm = () => {
        setEditingAgent(null);
        setAgentForm({ name: '', email: '', code: '' });
        setAgentFormError('');
        setShowAgentForm(true);
    };

    const openEditAgentForm = (agent) => {
        setEditingAgent(agent);
        setAgentForm({ name: agent.name || '', email: agent.email || '', code: agent.code || '' });
        setAgentFormError('');
        setShowAgentForm(true);
    };

    const handleSaveAgent = async () => {
        if (!agentForm.name.trim()) {
            setAgentFormError('Il nome del commerciale è obbligatorio.');
            return;
        }

        setAgentSaving(true);
        setAgentFormError('');
        try {
            if (editingAgent) {
                await commercialCommissionsService.updateAgent(editingAgent.id, agentForm);
            } else {
                await commercialCommissionsService.createAgent(agentForm);
            }
            setShowAgentForm(false);
            await loadAgents();
        } catch (err) {
            setAgentFormError(err.response?.data?.message || err.message || 'Errore nel salvataggio del commerciale');
        } finally {
            setAgentSaving(false);
        }
    };

    const handleToggleAgentActive = async (agent) => {
        const confirmingDisable = agent.active
            ? window.confirm('Il commerciale verrà disattivato. Gli storici già calcolati resteranno disponibili. Vuoi continuare?')
            : window.confirm('Vuoi riattivare questo commerciale?');

        if (!confirmingDisable) return;

        try {
            await commercialCommissionsService.updateAgent(agent.id, { active: !agent.active });
            await loadAgents();
        } catch (err) {
            alert(err.response?.data?.message || err.message || 'Errore nella modifica dello stato del commerciale.');
        }
    };

    const openNewRuleForm = () => {
        setEditingRule(null);
        setRuleForm({ tenantId: filterTenantId || '', commercialAgentId: '', commissionPercent: '' });
        setRuleFormError('');
        setShowRuleForm(true);
    };

    const openEditRuleForm = (rule) => {
        setEditingRule(rule);
        setRuleForm({
            tenantId: rule.tenantId || '',
            commercialAgentId: rule.commercialAgentId || '',
            commissionPercent: rule.commissionPercent != null ? String(rule.commissionPercent) : '',
        });
        setRuleFormError('');
        setShowRuleForm(true);
    };

    const handleSaveRule = async () => {
        const pct = Number(ruleForm.commissionPercent);
        const nextEffectiveMonth = getNextEffectiveYearMonth();

        if (!ruleForm.tenantId) {
            setRuleFormError('Seleziona un tenant.');
            return;
        }
        if (!ruleForm.commercialAgentId) {
            setRuleFormError('Seleziona un commerciale.');
            return;
        }
        if (ruleForm.commissionPercent === '') {
            setRuleFormError('La percentuale provvigionale è obbligatoria.');
            return;
        }
        if (Number.isNaN(pct)) {
            setRuleFormError('La percentuale provvigionale deve essere numerica.');
            return;
        }
        if (pct < 0 || pct > 20) {
            setRuleFormError('La percentuale provvigionale deve essere compresa tra 0 e 20.');
            return;
        }

        const duplicateRule = rules.find((rule) => (
            rule.id !== editingRule?.id
            && rule.tenantId === ruleForm.tenantId
            && rule.commercialAgentId === ruleForm.commercialAgentId
            && toYearMonthKey(rule.effectiveFromMonth) === nextEffectiveMonth
            && Math.abs(Number(rule.commissionPercent || 0) - pct) < 0.0001
            && rule.active !== false
        ));

        if (duplicateRule) {
            setRuleFormError('Esiste già una regola identica per questo tenant, commerciale e mese di decorrenza.');
            return;
        }

        const overlappingRules = rules.filter((rule) => {
            if (rule.id === editingRule?.id) return false;
            if (rule.tenantId !== ruleForm.tenantId) return false;

            const fromMonth = toYearMonthKey(rule.effectiveFromMonth);
            const toMonth = toYearMonthKey(rule.effectiveToMonth);
            const activeForTargetMonth = rule.active !== false
                && fromMonth <= nextEffectiveMonth
                && (!toMonth || toMonth > nextEffectiveMonth);

            if (!activeForTargetMonth) return false;
            if (rule.commercialAgentId === ruleForm.commercialAgentId) return false;
            return true;
        });

        const existingPercent = overlappingRules.reduce((sum, rule) => sum + Number(rule.commissionPercent || 0), 0);
        if (existingPercent + pct > 20.0001) {
            setRuleFormError('La somma delle percentuali provvigionali per questo tenant non può superare il 20%.');
            return;
        }

        setRuleSaving(true);
        setRuleFormError('');
        try {
            const payload = {
                tenantId: ruleForm.tenantId,
                commercialAgentId: ruleForm.commercialAgentId,
                commissionPercent: pct,
            };

            if (editingRule) {
                await commercialCommissionsService.updateRule(editingRule.id, payload);
            } else {
                await commercialCommissionsService.upsertRule(payload);
            }

            setShowRuleForm(false);
            setEditingRule(null);
            await loadRules();
            await loadMonthlyData();
        } catch (err) {
            setRuleFormError(err.response?.data?.message || err.message || 'Errore nel salvataggio della regola');
        } finally {
            setRuleSaving(false);
        }
    };

    const handleDeactivateRule = async (rule) => {
        const confirmed = window.confirm(
            'La regola verrà disattivata senza alterare gli storici già calcolati. Vuoi continuare?'
        );
        if (!confirmed) return;

        try {
            await commercialCommissionsService.deactivateRule(rule.id);
            await loadRules();
            await loadMonthlyData();
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Errore nella disattivazione della regola');
        }
    };

    const openManageModal = (tab = 'agents') => {
        setManageTab(tab);
        setShowAgentForm(false);
        setShowRuleForm(false);
        setEditingRule(null);
        loadRules();
        setShowManageModal(true);
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDir((prev) => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDir('asc');
        }
    };

    const filteredData = monthlyData.filter((item) => {
        if (filterTenantId && item.tenantId !== filterTenantId) return false;
        if (filterAgentId && item.commercialAgentId !== filterAgentId) return false;
        if (filterStatus && item.status !== filterStatus) return false;
        return true;
    });

    const sortedData = [...filteredData].sort((left, right) => {
        let valueLeft;
        let valueRight;

        if (sortBy === 'tenant') {
            valueLeft = left.tenant?.name || '';
            valueRight = right.tenant?.name || '';
        } else if (sortBy === 'month') {
            valueLeft = left.periodMonth || '';
            valueRight = right.periodMonth || '';
        } else if (sortBy === 'agent') {
            valueLeft = left.agent?.name || '';
            valueRight = right.agent?.name || '';
        } else if (sortBy === 'fee') {
            valueLeft = left.estimatedSoontipFeeCents || 0;
            valueRight = right.estimatedSoontipFeeCents || 0;
        } else if (sortBy === 'pct') {
            valueLeft = left.commissionPercentSnapshot || 0;
            valueRight = right.commissionPercentSnapshot || 0;
        } else if (sortBy === 'commission') {
            valueLeft = left.commissionAmountCents || 0;
            valueRight = right.commissionAmountCents || 0;
        } else if (sortBy === 'status') {
            valueLeft = left.status || '';
            valueRight = right.status || '';
        } else {
            valueLeft = 0;
            valueRight = 0;
        }

        if (valueLeft < valueRight) return sortDir === 'asc' ? -1 : 1;
        if (valueLeft > valueRight) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    const uniqueTenantRows = [];
    const seenTenantKeys = new Set();
    for (const item of filteredData) {
        const key = `${item.periodMonth}|${item.tenantId}`;
        if (!seenTenantKeys.has(key)) {
            seenTenantKeys.add(key);
            uniqueTenantRows.push(item);
        }
    }

    const totalCommission = filteredData.reduce((sum, row) => sum + (row.commissionAmountCents || 0), 0);
    const totalFee = uniqueTenantRows.reduce((sum, row) => sum + (row.estimatedSoontipFeeCents || 0), 0);
    const totalResidual = totalFee - totalCommission;

    const availableVenues = filterTenantId
        ? venues.filter((venue) => venue.tenantId === filterTenantId)
        : venues;

    const availableDevices = devices.filter((device) => {
        if (filterTenantId && device.tenantId !== filterTenantId) return false;
        if (filterVenueId && device.venueId !== filterVenueId) return false;
        return true;
    });

    const activeAgents = agents.filter((agent) => agent.active);

    return (
        <div className="cc-page">
            <div className="cc-header">
                <div className="cc-header-left">
                    <Percent size={26} className="cc-header-icon" />
                    <div>
                        <h1>Provvigioni commerciali</h1>
                        <p className="cc-subtitle">Calcolo, storico ed export delle provvigioni per tenant</p>
                    </div>
                </div>
                <div className="cc-header-actions">
                    <button className="cc-btn cc-btn-ghost" onClick={() => openManageModal('agents')}>
                        <Users size={15} />
                        Gestisci commerciali
                    </button>
                    <button className="cc-btn cc-btn-ghost" onClick={() => openManageModal('rules')}>
                        <Edit size={15} />
                        Regole provvigionali
                    </button>
                </div>
            </div>

            <PageGuide
                title="Come funzionano le provvigioni commerciali"
                summary={
                    <div className="cc-guide-content">
                        <p>
                            <strong>Base di calcolo:</strong> Fee Soontip stimata del mese. Il GTV è mostrato solo a titolo informativo e non viene usato per calcolare le provvigioni. Le fatture emesse non sono usate come base di calcolo.
                        </p>
                        <div className="cc-guide-flow">
                            <strong>Flusso</strong>
                            <ol>
                                <li>Crea un commerciale</li>
                                <li>Associa il commerciale a un tenant con una percentuale</li>
                                <li>Seleziona il mese</li>
                                <li>Genera o aggiorna il calcolo</li>
                                <li>Verifica gli importi</li>
                                <li>Esporta il prospetto</li>
                                <li>Segna le quote come pagate</li>
                            </ol>
                        </div>
                        <p className="cc-guide-note">
                            <strong>Quando usare la retroattiva:</strong> la generazione standard è pensata per il mese corrente/operativo.
                            Per mesi storici senza snapshot usare <strong>Genera retroattivo</strong>, che applica fallback alle regole correnti/ultime note se nel mese target non esistono regole attive.
                        </p>
                        <p className="cc-guide-note">
                            Le modifiche alle percentuali valgono dal mese successivo. Gli snapshot già pagati non vengono sovrascritti.
                        </p>
                    </div>
                }
            />

            <div className="cc-control-bar">
                <div className="cc-control-left">
                    <select
                        value={periodPreset}
                        onChange={(event) => setPeriodPreset(event.target.value)}
                        className="cc-select"
                        style={{ minWidth: 180 }}
                    >
                        <option value="EXACT_MONTH">Mese specifico</option>
                        <option value="LAST_MONTH">Ultimo mese</option>
                        <option value="LAST_3_MONTHS">Ultimi 3 mesi</option>
                        <option value="ALL">Tutti</option>
                    </select>
                    <div className="cc-month-picker">
                        <label htmlFor="cc-month-input">Mese</label>
                        <input
                            id="cc-month-input"
                            type="month"
                            value={selectedMonth}
                            onChange={(event) => setSelectedMonth(event.target.value)}
                            disabled={periodPreset !== 'EXACT_MONTH'}
                        />
                    </div>
                    <button
                        className="cc-btn cc-btn-primary"
                        onClick={handleGenerate}
                        disabled={generating || generatingRetroactive || !isExactMonthMode}
                        title={!isExactMonthMode ? 'Disponibile solo con filtro "Mese specifico"' : undefined}
                    >
                        <RefreshCw size={15} className={generating ? 'cc-spin' : ''} />
                        {generating ? 'Generazione...' : 'Genera / aggiorna calcolo'}
                    </button>
                    <button
                        className="cc-btn cc-btn-ghost"
                        onClick={handleGenerateRetroactive}
                        disabled={generating || generatingRetroactive || !isExactMonthMode}
                        title={!isExactMonthMode ? 'Disponibile solo con filtro "Mese specifico"' : undefined}
                    >
                        <RefreshCw size={15} className={generatingRetroactive ? 'cc-spin' : ''} />
                        {generatingRetroactive ? 'Retroattiva in corso...' : 'Genera retroattivo'}
                    </button>
                </div>
                <div className="cc-control-right">
                    <button
                        className="cc-btn cc-btn-ghost"
                        onClick={handleExport}
                        disabled={exporting || filteredData.length === 0}
                    >
                        <Download size={15} />
                        {exporting ? 'Export in corso...' : periodPreset === 'EXACT_MONTH' ? 'Esporta CSV' : 'Esporta CSV (dati visibili)'}
                    </button>
                </div>
            </div>

            <div className="cc-filters-card">
                <div className="cc-filters-title">
                    <Filter size={13} />
                    Filtri
                </div>
                <div className="cc-filters-row">
                    <select
                        value={filterTenantId}
                        onChange={(event) => {
                            setFilterTenantId(event.target.value);
                            setFilterVenueId('');
                            setFilterDeviceId('');
                        }}
                        className="cc-select"
                    >
                        <option value="">Tutti i tenant</option>
                        {tenants.map((tenant) => (
                            <option key={tenant.id} value={tenant.id}>{tenant.adminAlias || tenant.company_name || tenant.name || tenant.id}</option>
                        ))}
                    </select>
                    <select
                        value={filterVenueId}
                        onChange={(event) => {
                            setFilterVenueId(event.target.value);
                            setFilterDeviceId('');
                        }}
                        className="cc-select"
                    >
                        <option value="">Tutti i locali / venue</option>
                        {availableVenues.map((venue) => (
                            <option key={venue.id} value={venue.id}>{venue.name}</option>
                        ))}
                    </select>
                    <select
                        value={filterDeviceId}
                        onChange={(event) => setFilterDeviceId(event.target.value)}
                        className="cc-select"
                    >
                        <option value="">Tutti i totem / device</option>
                        {availableDevices.map((device) => (
                            <option key={device.id} value={device.id}>
                                {device.label || device.setupCode || device.id}
                            </option>
                        ))}
                    </select>
                    <select
                        value={filterAgentId}
                        onChange={(event) => setFilterAgentId(event.target.value)}
                        className="cc-select"
                    >
                        <option value="">Tutti i commerciali</option>
                        {activeAgents.map((agent) => (
                            <option key={agent.id} value={agent.id}>
                                {agent.name}{agent.code ? ` (${agent.code})` : ''}
                            </option>
                        ))}
                    </select>
                    <select
                        value={filterStatus}
                        onChange={(event) => setFilterStatus(event.target.value)}
                        className="cc-select"
                    >
                        <option value="">Tutti gli stati</option>
                        <option value="CALCULATED">Da pagare</option>
                        <option value="PAID">Pagata</option>
                    </select>
                    <button className="cc-btn cc-btn-ghost" onClick={loadMonthlyData} disabled={loading}>
                        <RefreshCw size={13} className={loading ? 'cc-spin' : ''} />
                        Aggiorna
                    </button>
                </div>
                <div className="cc-filter-note">
                    Venue e device sono usati come breakdown informativo e come filtro di visibilità/export; il calcolo della provvigione resta sul totale tenant del mese.
                </div>
            </div>

            {error && (
                <div className="cc-alert cc-alert-error">
                    <AlertCircle size={15} />
                    <span>{error}</span>
                    <button className="cc-alert-close" onClick={() => setError(null)}>
                        <X size={13} />
                    </button>
                </div>
            )}

            {!loading && filteredData.length > 0 && (
                <div className="cc-summary-row">
                    <div className="cc-summary-card">
                        <span className="cc-summary-label">Righe visualizzate</span>
                        <span className="cc-summary-value">{filteredData.length}</span>
                    </div>
                    <div className="cc-summary-card">
                        <span className="cc-summary-label">Fee Soontip stimata totale</span>
                        <span className="cc-summary-value">{formatCurrency(totalFee)}</span>
                    </div>
                    <div className="cc-summary-card cc-summary-card-accent">
                        <span className="cc-summary-label">Totale provvigioni</span>
                        <span className="cc-summary-value">{formatCurrency(totalCommission)}</span>
                    </div>
                    <div className="cc-summary-card cc-summary-card-success">
                        <span className="cc-summary-label">Residuo Soontip dopo provvigioni</span>
                        <span className="cc-summary-value">{formatCurrency(totalResidual)}</span>
                    </div>
                </div>
            )}

            <div className="cc-table-card">
                {loading ? (
                    <div className="cc-loading">
                        <div className="cc-spinner" />
                        <p>Caricamento dati...</p>
                    </div>
                ) : filteredData.length === 0 ? (
                    <div className="cc-empty">
                        <Percent size={40} className="cc-empty-icon" />
                        <p className="cc-empty-title">Nessun dato per i filtri selezionati</p>
                        <p className="cc-empty-sub">
                            Seleziona il mese corretto, verifica che esistano regole attive e clicca <strong>Genera / aggiorna calcolo</strong>.
                        </p>
                    </div>
                ) : (
                    <div className="cc-table-wrap">
                        <table className="cc-table">
                            <thead>
                                <tr>
                                    <SortTh field="tenant" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Tenant</SortTh>
                                    <SortTh field="month" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Mese</SortTh>
                                    <SortTh field="agent" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Commerciale</SortTh>
                                    <SortTh field="pct" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} alignRight>%</SortTh>
                                    <SortTh field="commission" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} alignRight>Provvigione</SortTh>
                                    <SortTh field="fee" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} alignRight>Fee Soontip stimata</SortTh>
                                    <SortTh field="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Stato</SortTh>
                                    <th>Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.map((item) => {
                                    const isExpanded = expandedRowId === item.id;
                                    return (
                                        <Fragment key={item.id}>
                                            <tr className={`${item.status === 'PAID' ? 'cc-row-paid' : ''}${isExpanded ? ' cc-row-expanded' : ''}`}>
                                                <td className="cc-td-tenant">{item.tenant?.name || item.tenantId}</td>
                                                <td>{formatMonth(item.periodMonth)}</td>
                                                <td>
                                                    <span className="cc-agent-name">{item.agent?.name || item.commercialAgentId}</span>
                                                    {item.agent?.code && <span className="cc-agent-code">{item.agent.code}</span>}
                                                </td>
                                                <td className="cc-td-right">{formatPercent(item.commissionPercentSnapshot)}</td>
                                                <td className="cc-td-right cc-commission">{formatCurrency(item.commissionAmountCents)}</td>
                                                <td className="cc-td-right cc-bold">{formatCurrency(item.estimatedSoontipFeeCents)}</td>
                                                <td><StatusBadge status={item.status} /></td>
                                                <td className="cc-td-actions">
                                                    <button
                                                        className={`cc-btn-expand ${isExpanded ? 'cc-btn-expand-open' : ''}`}
                                                        onClick={() => setExpandedRowId(isExpanded ? null : item.id)}
                                                        title={isExpanded ? 'Chiudi dettaglio' : 'Vedi dettaglio'}
                                                    >
                                                        <ChevronDown size={14} />
                                                    </button>
                                                    {item.status === 'CALCULATED' ? (
                                                        <button
                                                            className="cc-btn-action cc-btn-pay"
                                                            onClick={() => setConfirmPaidItem(item)}
                                                            title="Segna come pagata"
                                                        >
                                                            <Check size={13} />
                                                            Segna come pagata
                                                        </button>
                                                    ) : (
                                                        <span className="cc-paid-label">
                                                            <CheckCircle size={13} />
                                                            Pagata
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className="cc-detail-row">
                                                    <td colSpan={8}>
                                                        <div className="cc-detail-grid">
                                                            <div className="cc-detail-item">
                                                                <span className="cc-detail-label">Mese</span>
                                                                <span className="cc-detail-value">{formatMonth(item.periodMonth)}</span>
                                                            </div>
                                                            <div className="cc-detail-item">
                                                                <span className="cc-detail-label">GTV mensile</span>
                                                                <span className="cc-detail-value cc-muted">{formatCurrency(item.gtvCents)}</span>
                                                            </div>
                                                            <div className="cc-detail-item">
                                                                <span className="cc-detail-label">Totale % assegnata</span>
                                                                <span className="cc-detail-value">{formatPercent(item.totalPercentAssigned)}</span>
                                                            </div>
                                                            <div className="cc-detail-item">
                                                                <span className="cc-detail-label">Totale provvigioni tenant</span>
                                                                <span className="cc-detail-value cc-commission">{formatCurrency(item.totalCommissionCents)}</span>
                                                            </div>
                                                            <div className="cc-detail-item">
                                                                <span className="cc-detail-label">Residuo Soontip dopo provvigioni</span>
                                                                <span className="cc-detail-value cc-residual">{formatCurrency(item.residualSoontipAfterCommissionsCents)}</span>
                                                            </div>
                                                            <div className="cc-detail-item">
                                                                <span className="cc-detail-label">Commerciali associati</span>
                                                                <span className="cc-detail-value">{item.commercialAgentsCount || 0}</span>
                                                            </div>
                                                            <div className="cc-detail-item">
                                                                <span className="cc-detail-label">Data pagamento</span>
                                                                <span className="cc-detail-value">{formatDate(item.paidAt)}</span>
                                                            </div>
                                                            <div className="cc-detail-item">
                                                                <span className="cc-detail-label">Data calcolo</span>
                                                                <span className="cc-detail-value">{formatDate(item.calculatedAt)}</span>
                                                            </div>
                                                            {item.paidByAdminId && (
                                                                <div className="cc-detail-item">
                                                                    <span className="cc-detail-label">Admin pagamento</span>
                                                                    <span className="cc-detail-value">{item.paidByAdminId}</span>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="cc-detail-sections">
                                                            <div className="cc-detail-panel">
                                                                <div className="cc-detail-panel-title">Quote per commerciale</div>
                                                                <div className="cc-split-list">
                                                                    {(item.tenantCommissionQuotes || []).map((quote) => (
                                                                        <div key={quote.id} className="cc-split-row">
                                                                            <div>
                                                                                <strong>{quote.agentName}</strong>
                                                                                {quote.agentCode && <span className="cc-agent-code">{quote.agentCode}</span>}
                                                                            </div>
                                                                            <div className="cc-split-values">
                                                                                <span>{formatPercent(quote.commissionPercentSnapshot)}</span>
                                                                                <span className="cc-commission">{formatCurrency(quote.commissionAmountCents)}</span>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            <div className="cc-detail-panel">
                                                                <div className="cc-detail-panel-title">Breakdown locali / device</div>
                                                                {(item.venueBreakdown || []).length === 0 ? (
                                                                    <div className="cc-breakdown-empty">Nessun breakdown venue/device disponibile per i filtri selezionati.</div>
                                                                ) : (
                                                                    <div className="cc-breakdown-list">
                                                                        {item.venueBreakdown.map((venue) => (
                                                                            <div key={venue.venueId} className="cc-breakdown-venue">
                                                                                <div className="cc-breakdown-venue-head">
                                                                                    <strong>{venue.venueName}</strong>
                                                                                    <span>Fee stimata {formatCurrency(venue.estimatedSoontipFeeCents)}</span>
                                                                                    <span>GTV {formatCurrency(venue.gtvCents)}</span>
                                                                                </div>
                                                                                {(venue.devices || []).length > 0 ? (
                                                                                    <div className="cc-breakdown-devices">
                                                                                        {venue.devices.map((device) => (
                                                                                            <span key={device.deviceId} className="cc-breakdown-device-chip">
                                                                                                {device.deviceLabel}
                                                                                            </span>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="cc-breakdown-empty cc-breakdown-empty-inline">Nessun dettaglio device disponibile.</div>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && filteredData.length > 0 && (
                    <div className="cc-table-footer">
                        {sortedData.length} rig{sortedData.length === 1 ? 'a' : 'he'}
                        {filterStatus === 'PAID' && ' · solo pagate'}
                        {filterStatus === 'CALCULATED' && ' · solo da pagare'}
                    </div>
                )}
            </div>

            {confirmPaidItem && (
                <div className="cc-overlay" onClick={() => !markingPaid && setConfirmPaidItem(null)}>
                    <div className="cc-modal cc-modal-sm" onClick={(event) => event.stopPropagation()}>
                        <div className="cc-modal-header">
                            <div className="cc-modal-title">
                                <Check size={18} className="cc-modal-icon cc-icon-success" />
                                Conferma pagamento
                            </div>
                            <button
                                className="cc-modal-close"
                                onClick={() => !markingPaid && setConfirmPaidItem(null)}
                                disabled={markingPaid}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="cc-modal-body">
                            <p>
                                Confermi di voler segnare come pagata questa quota provvigionale? L’azione riguarda solo questo commerciale, tenant e mese.
                            </p>
                            <div className="cc-confirm-details">
                                <div className="cc-confirm-row">
                                    <span className="cc-confirm-label">Tenant</span>
                                    <strong>{confirmPaidItem.tenant?.name}</strong>
                                </div>
                                <div className="cc-confirm-row">
                                    <span className="cc-confirm-label">Commerciale</span>
                                    <strong>{confirmPaidItem.agent?.name}</strong>
                                </div>
                                <div className="cc-confirm-row">
                                    <span className="cc-confirm-label">Mese</span>
                                    <strong>{formatMonth(confirmPaidItem.periodMonth)}</strong>
                                </div>
                                <div className="cc-confirm-row">
                                    <span className="cc-confirm-label">Importo</span>
                                    <strong className="cc-confirm-amount">{formatCurrency(confirmPaidItem.commissionAmountCents)}</strong>
                                </div>
                            </div>
                        </div>
                        <div className="cc-modal-footer">
                            <button className="cc-btn cc-btn-ghost" onClick={() => setConfirmPaidItem(null)} disabled={markingPaid}>
                                Annulla
                            </button>
                            <button className="cc-btn cc-btn-success" onClick={handleMarkPaid} disabled={markingPaid}>
                                <Check size={15} />
                                {markingPaid ? 'Salvataggio...' : 'Segna come pagata'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showManageModal && (
                <div className="cc-overlay" onClick={() => setShowManageModal(false)}>
                    <div className="cc-modal cc-modal-lg" onClick={(event) => event.stopPropagation()}>
                        <div className="cc-modal-header">
                            <div className="cc-modal-title">
                                <Users size={18} className="cc-modal-icon" />
                                Gestione commerciali e regole
                            </div>
                            <button className="cc-modal-close" onClick={() => setShowManageModal(false)}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="cc-modal-tabs">
                            <button
                                className={`cc-tab ${manageTab === 'agents' ? 'cc-tab-active' : ''}`}
                                onClick={() => {
                                    setManageTab('agents');
                                    setShowAgentForm(false);
                                }}
                            >
                                <Users size={14} />
                                Commerciali
                            </button>
                            <button
                                className={`cc-tab ${manageTab === 'rules' ? 'cc-tab-active' : ''}`}
                                onClick={() => {
                                    setManageTab('rules');
                                    setShowRuleForm(false);
                                }}
                            >
                                <Percent size={14} />
                                Regole provvigionali
                            </button>
                        </div>

                        <div className="cc-modal-body">
                            {manageTab === 'agents' && (
                                <div className="cc-panel">
                                    {!showAgentForm ? (
                                        <>
                                            <div className="cc-panel-toolbar">
                                                <button className="cc-btn cc-btn-primary cc-btn-sm" onClick={openNewAgentForm}>
                                                    <Plus size={13} />
                                                    Nuovo commerciale
                                                </button>
                                            </div>
                                            <table className="cc-table cc-table-compact">
                                                <thead>
                                                    <tr>
                                                        <th>Nome</th>
                                                        <th>Email</th>
                                                        <th>Codice</th>
                                                        <th>Stato</th>
                                                        <th>Azioni</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {agents.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={5} className="cc-empty-cell">Nessun commerciale registrato</td>
                                                        </tr>
                                                    ) : agents.map((agent) => (
                                                        <tr key={agent.id} className={!agent.active ? 'cc-row-inactive' : ''}>
                                                            <td className="cc-td-name">{agent.name}</td>
                                                            <td>{agent.email || '—'}</td>
                                                            <td>{agent.code || '—'}</td>
                                                            <td>
                                                                <span className={`cc-badge ${agent.active ? 'cc-badge-active' : 'cc-badge-inactive'}`}>
                                                                    {agent.active ? 'Attivo' : 'Disattivato'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="cc-row-btns">
                                                                    <button className="cc-btn-action" onClick={() => openEditAgentForm(agent)} title="Modifica">
                                                                        <Edit size={13} />
                                                                        Modifica
                                                                    </button>
                                                                    <button
                                                                        className={`cc-btn-action ${agent.active ? 'cc-btn-deactivate' : 'cc-btn-activate'}`}
                                                                        onClick={() => handleToggleAgentActive(agent)}
                                                                        title={agent.active ? 'Disattiva' : 'Riattiva'}
                                                                    >
                                                                        {agent.active ? <X size={13} /> : <Check size={13} />}
                                                                        {agent.active ? 'Disattiva' : 'Riattiva'}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </>
                                    ) : (
                                        <div className="cc-inline-form">
                                            <h3 className="cc-form-title">{editingAgent ? 'Modifica commerciale' : 'Nuovo commerciale'}</h3>
                                            {agentFormError && (
                                                <div className="cc-alert cc-alert-error cc-alert-sm">
                                                    <AlertCircle size={13} />
                                                    {agentFormError}
                                                </div>
                                            )}
                                            <div className="cc-form-grid">
                                                <div className="cc-form-field">
                                                    <label>Nome *</label>
                                                    <input
                                                        type="text"
                                                        value={agentForm.name}
                                                        onChange={(event) => setAgentForm((prev) => ({ ...prev, name: event.target.value }))}
                                                        placeholder="Nome completo"
                                                    />
                                                </div>
                                                <div className="cc-form-field">
                                                    <label>Email</label>
                                                    <input
                                                        type="email"
                                                        value={agentForm.email}
                                                        onChange={(event) => setAgentForm((prev) => ({ ...prev, email: event.target.value }))}
                                                        placeholder="email@esempio.com"
                                                    />
                                                </div>
                                                <div className="cc-form-field">
                                                    <label>Codice commerciale</label>
                                                    <input
                                                        type="text"
                                                        value={agentForm.code}
                                                        onChange={(event) => setAgentForm((prev) => ({ ...prev, code: event.target.value }))}
                                                        placeholder="Es. EM01"
                                                    />
                                                </div>
                                            </div>
                                            <div className="cc-form-actions">
                                                <button className="cc-btn cc-btn-ghost" onClick={() => setShowAgentForm(false)}>Annulla</button>
                                                <button className="cc-btn cc-btn-primary" onClick={handleSaveAgent} disabled={agentSaving}>
                                                    {agentSaving ? 'Salvataggio...' : editingAgent ? 'Aggiorna commerciale' : 'Crea commerciale'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {manageTab === 'rules' && (
                                <div className="cc-panel">
                                    <div className="cc-rules-notice">
                                        <AlertCircle size={14} />
                                        <span>
                                            Le modifiche alle percentuali saranno valide dal <strong>mese successivo</strong>. Gli storici già calcolati o pagati non vengono alterati. La somma delle percentuali per tenant non può superare il <strong>20%</strong>.
                                        </span>
                                    </div>

                                    {!showRuleForm ? (
                                        <>
                                            <div className="cc-panel-toolbar">
                                                <button className="cc-btn cc-btn-primary cc-btn-sm" onClick={openNewRuleForm}>
                                                    <Plus size={13} />
                                                    Nuova regola
                                                </button>
                                            </div>

                                            <table className="cc-table cc-table-compact">
                                                <thead>
                                                    <tr>
                                                        <th>Tenant</th>
                                                        <th>Commerciale</th>
                                                        <th>Codice</th>
                                                        <th className="cc-th-right">Percentuale</th>
                                                        <th>Mese decorrenza</th>
                                                        <th>Mese fine</th>
                                                        <th>Stato</th>
                                                        <th>Azioni</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rules.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={8} className="cc-empty-cell">Nessuna regola configurata</td>
                                                        </tr>
                                                    ) : rules.map((rule) => {
                                                        const statusMeta = getRuleStatusMeta(rule);
                                                        const actionsEnabled = statusMeta.label !== 'Non attiva';
                                                        return (
                                                            <tr key={rule.id} className={statusMeta.label === 'Non attiva' ? 'cc-row-inactive' : ''}>
                                                                <td>{rule.tenant?.adminAlias || rule.tenant?.company_name || rule.tenantId}</td>
                                                                <td>{rule.commercialAgent?.name || rule.commercialAgentId}</td>
                                                                <td>{rule.commercialAgent?.code || '—'}</td>
                                                                <td className="cc-td-right">{formatPercent(rule.commissionPercent)}</td>
                                                                <td>{formatMonth(rule.effectiveFromMonth)}</td>
                                                                <td>{rule.effectiveToMonth ? formatMonth(rule.effectiveToMonth) : '—'}</td>
                                                                <td>
                                                                    <span className={`cc-badge ${statusMeta.className}`}>{statusMeta.label}</span>
                                                                </td>
                                                                <td>
                                                                    {actionsEnabled ? (
                                                                        <div className="cc-row-btns">
                                                                            <button className="cc-btn-action" onClick={() => openEditRuleForm(rule)}>
                                                                                <Edit size={13} />
                                                                                Modifica
                                                                            </button>
                                                                            <button className="cc-btn-action cc-btn-deactivate" onClick={() => handleDeactivateRule(rule)}>
                                                                                <X size={13} />
                                                                                Disattiva
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <span className="cc-muted">—</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </>
                                    ) : (
                                        <div className="cc-inline-form">
                                            <h3 className="cc-form-title">{editingRule ? 'Modifica regola provvigionale' : 'Nuova regola provvigionale'}</h3>
                                            <p className="cc-form-note">
                                                La regola sarà valida a partire da <strong>{formatMonth(getNextEffectiveYearMonth())}</strong>.
                                            </p>
                                            {ruleFormError && (
                                                <div className="cc-alert cc-alert-error cc-alert-sm">
                                                    <AlertCircle size={13} />
                                                    {ruleFormError}
                                                </div>
                                            )}
                                            <div className="cc-form-grid">
                                                <div className="cc-form-field">
                                                    <label>Tenant *</label>
                                                    <select
                                                        value={ruleForm.tenantId}
                                                        onChange={(event) => setRuleForm((prev) => ({ ...prev, tenantId: event.target.value }))}
                                                    >
                                                        <option value="">Seleziona tenant</option>
                                                        {tenants.map((tenant) => (
                                                            <option key={tenant.id} value={tenant.id}>
                                                                {tenant.adminAlias || tenant.company_name || tenant.name || tenant.id}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="cc-form-field">
                                                    <label>Commerciale *</label>
                                                    <select
                                                        value={ruleForm.commercialAgentId}
                                                        onChange={(event) => setRuleForm((prev) => ({ ...prev, commercialAgentId: event.target.value }))}
                                                    >
                                                        <option value="">Seleziona commerciale</option>
                                                        {activeAgents.map((agent) => (
                                                            <option key={agent.id} value={agent.id}>
                                                                {agent.name}{agent.code ? ` (${agent.code})` : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="cc-form-field">
                                                    <label>Percentuale provvigionale (%) *</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="20"
                                                        step="0.01"
                                                        value={ruleForm.commissionPercent}
                                                        onChange={(event) => setRuleForm((prev) => ({ ...prev, commissionPercent: event.target.value }))}
                                                        placeholder="Es. 5.00"
                                                    />
                                                    <span className="cc-field-hint">
                                                        Percentuale assoluta sulla Fee Soontip stimata. Il totale per tenant non può superare il 20%.
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="cc-form-actions">
                                                <button className="cc-btn cc-btn-ghost" onClick={() => { setShowRuleForm(false); setEditingRule(null); }}>
                                                    Annulla
                                                </button>
                                                <button className="cc-btn cc-btn-primary" onClick={handleSaveRule} disabled={ruleSaving}>
                                                    {ruleSaving ? 'Salvataggio...' : editingRule ? 'Aggiorna regola' : 'Salva regola'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
